import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { apiClient } from "src/dm/utils/axios";

interface CategoryTeamPreference {
  categoryManagerCode: number;
  apVendorNumber: string;
  defaultAdFee: number | null;
  defaultAdminFee: number | null;
  reflectStartWeekday: number | null;
  promoStartWeekday: number | null;
}

interface PrefsResponse {
  items: CategoryTeamPreference[];
  total: number;
}

const baseUrl = "/dm/V1/category-team-preferences";

const CategoryTeamPrefs = () => {
  const [filterCatMgr, setFilterCatMgr] = useState<number | null>(null);
  const [editRow, setEditRow] = useState<CategoryTeamPreference | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["category-team-prefs", filterCatMgr],
    queryFn: async (): Promise<CategoryTeamPreference[]> => {
      const response = await apiClient.get<PrefsResponse>(baseUrl, {
        params: { categoryManagerCode: filterCatMgr ?? undefined },
      });
      return response.data.items;
    },
  });

  const save = useMutation({
    mutationFn: async (row: CategoryTeamPreference) => {
      const response = await apiClient.put<CategoryTeamPreference>(
        `${baseUrl}/${row.categoryManagerCode}/${encodeURIComponent(row.apVendorNumber)}`,
        {
          defaultAdFee: row.defaultAdFee,
          defaultAdminFee: row.defaultAdminFee,
          reflectStartWeekday: row.reflectStartWeekday,
          promoStartWeekday: row.promoStartWeekday,
        }
      );
      return response.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["category-team-prefs"] }),
  });

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as CategoryTeamPreference;
    return (
      <td>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          onClick={() => setEditRow({ ...row })}
        >
          Edit
        </Button>
      </td>
    );
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <h1 style={{ marginTop: 0 }}>Category Team Preferences</h1>
      <p style={{ color: "#475569", marginTop: "-0.5rem" }}>
        Per category-manager + AP vendor defaults (fees, weekday start). Drives
        New-Deal field defaults.
      </p>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          display: "flex",
          gap: "0.75rem",
          alignItems: "end",
        }}
      >
        <label>
          <span style={{ fontSize: "0.8rem", color: "#475569" }}>
            Filter by Cat Mgr code
          </span>
          <NumericTextBox
            value={filterCatMgr}
            onChange={(e) => setFilterCatMgr(e.value ?? null)}
          />
        </label>
        <Button type="button" onClick={() => setFilterCatMgr(null)}>
          Clear
        </Button>
      </section>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {isLoading && <p>Loading…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}
        {data && (data.length === 0 ? (
          <p style={{ color: "#94a3b8" }}>No rows.</p>
        ) : (
          <Grid data={data}>
            <GridColumn field="categoryManagerCode" title="Cat Mgr" width="100px" />
            <GridColumn field="apVendorNumber" title="AP Vendor" width="140px" />
            <GridColumn field="defaultAdFee" title="Default Ad Fee" width="140px" format="{0:c2}" />
            <GridColumn field="defaultAdminFee" title="Default Admin Fee" width="160px" format="{0:c2}" />
            <GridColumn field="reflectStartWeekday" title="Reflect Start Day" width="140px" />
            <GridColumn field="promoStartWeekday" title="Promo Start Day" width="140px" />
            <GridColumn title="" width="80px" cells={{ data: ActionsCell }} />
          </Grid>
        ))}
      </section>

      {editRow && (
        <Dialog
          title={`Edit ${editRow.categoryManagerCode} / ${editRow.apVendorNumber}`}
          onClose={() => setEditRow(null)}
        >
          <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: "1fr 1fr" }}>
            <label>
              <span style={{ fontSize: "0.8rem", color: "#475569" }}>Default Ad Fee</span>
              <NumericTextBox
                format="c2"
                value={editRow.defaultAdFee ?? null}
                onChange={(e) =>
                  setEditRow({ ...editRow, defaultAdFee: e.value ?? null })
                }
              />
            </label>
            <label>
              <span style={{ fontSize: "0.8rem", color: "#475569" }}>Default Admin Fee</span>
              <NumericTextBox
                format="c2"
                value={editRow.defaultAdminFee ?? null}
                onChange={(e) =>
                  setEditRow({ ...editRow, defaultAdminFee: e.value ?? null })
                }
              />
            </label>
            <label>
              <span style={{ fontSize: "0.8rem", color: "#475569" }}>Reflect Start Weekday (1-7)</span>
              <NumericTextBox
                min={1}
                max={7}
                value={editRow.reflectStartWeekday ?? null}
                onChange={(e) =>
                  setEditRow({ ...editRow, reflectStartWeekday: e.value ?? null })
                }
              />
            </label>
            <label>
              <span style={{ fontSize: "0.8rem", color: "#475569" }}>Promo Start Weekday (1-7)</span>
              <NumericTextBox
                min={1}
                max={7}
                value={editRow.promoStartWeekday ?? null}
                onChange={(e) =>
                  setEditRow({ ...editRow, promoStartWeekday: e.value ?? null })
                }
              />
            </label>
          </div>
          <DialogActionsBar>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={save.isPending}
              onClick={async () => {
                await save.mutateAsync(editRow);
                setEditRow(null);
              }}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
          {save.isError && (
            <pre style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
              {save.error instanceof Error ? save.error.message : "Save failed"}
            </pre>
          )}
        </Dialog>
      )}
    </div>
  );
};

export default CategoryTeamPrefs;
